/* global __SERVER__ */

import React, { Component } from 'react';
import { compose } from 'redux';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { func, string, object, array, bool, shape } from 'prop-types';
import { fromEvent, Subject, of } from 'rxjs';
import {
  takeUntil,
  concatMap,
  filter,
  pairwise,
  map,
  mergeMap,
} from 'rxjs/operators';

import { getMultipleOfferingMode } from '@blueberry/redux/siteInfo';
import { SnapaskLoader } from '@snapask/common-components';

import MainLayout from '@blueberry/layouts/MainLayout';
import SEOHead from '@blueberry/components/SEOHead';

import PostList from '@blueberry/components/PostList';
import CTAFooter from '@blueberry/components/CTAFooter';
import TrendBlock from './components/TrendBlock';
import Banner from '@blueberry/components/Banner';
import ChannelBar from '@blueberry/components/ChannelBar';
import NotFound from '@blueberry/components/NotFound';
import Breadcrumb from '@blueberry/components/Breadcrumb';

import {
  getStatus,
  getTrendPosts,
  getLatestPosts,
  getTopicPosts,
  resetHome,
  fetchNextPage,
  getNeedLoadMore,
  getPageDisplayName,
} from '@blueberry/redux/home';
import { getBanner } from '@blueberry/redux/banner';
import { getChannels } from '@blueberry/redux/channel';
import { getUserID } from '@blueberry/redux/profile';
import { getIsInapp } from '@blueberry/redux/location';
import { getLocaleCode } from '@blueberry/redux/locale';
import { getPagePosts } from '@blueberry/redux/pagePost';

import { fetchHomeFlow } from '@blueberry/sagas/home';

import { LOADING, ERROR } from '@blueberry/constants/loadingState';
import { VIDEO } from '@blueberry/constants/postType';

import { haveObjectProperties } from '@blueberry/utils/utility';

import styles from './Home.scss';

const mapStateToProps = state => ({
  banner: getBanner(state),
  channels: getChannels(state),
  status: getStatus(state),
  userId: getUserID(state),
  isInApp: getIsInapp(state),
  latestPosts: getLatestPosts(state),
  topicPosts: getTopicPosts(state),
  trendPosts: getTrendPosts(state),
  needLoadMore: getNeedLoadMore(state),
  locale: getLocaleCode(state),
  posts: getPagePosts(state),
  userId: getUserID(state),
  multipleOfferingMode: getMultipleOfferingMode(state),
  displayName: getPageDisplayName(state),
});

class Home extends Component {
  static prefetch = ({ name }) => [fetchHomeFlow, { payload: { name } }];

  static propTypes = {
    fetchNextPage: func.isRequired,
    isInApp: bool.isRequired,
    latestPosts: array.isRequired,
    locale: string.isRequired,
    match: shape({
      params: object.isRequired,
    }).isRequired,
    multipleOfferingMode: bool.isRequired,
    needLoadMore: bool.isRequired,
    resetHome: func.isRequired,
    status: string.isRequired,
    t: func.isRequired,
    topicPosts: array.isRequired,
    trendPosts: array.isRequired,
    banner: object,
    channels: array,
    displayName: string,
    posts: array,
    userId: string,
  };

  static contextTypes = {
    region: string,
  };

  static defaultProps = {
    banner: {},
    channels: [],
    posts: [],
    userId: '',
    displayName: '',
  };

  componentDidMount() {
    const {
      fetchNextPage,
      match: {
        params: { name },
      },
    } = this.props;

    if (!name) return;

    fromEvent(window, 'scroll')
      .pipe(this.handleScrolling)
      .subscribe(() => {
        if (this.props.status === LOADING) {
          return;
        }
        fetchNextPage();
      });
  }

  componentWillUnmount() {
    this.destory$.next();
  }

  destory$ = new Subject();

  handleScrolling = obs =>
    obs.pipe(
      mergeMap(() => of(this.props.needLoadMore).pipe(filter(p => p))),
      takeUntil(this.destory$),
      map(() => window.pageYOffset),
      pairwise(),
      // only handle the direction to down
      filter(([y1, y2]) => y2 > y1),
      map(([, y2]) => y2),
      concatMap(height =>
        of(this.node.getBoundingClientRect()).pipe(
          filter(rect => rect.bottom < height)
        )
      )
    );

  renderChannels = channels => {
    const { t } = this.props;
    return channels
      .filter(channel => channel.posts.length > 0)
      .map(channel => (
        <ChannelBar
          hintText={t('home:home.viewAll')}
          key={channel.name}
          {...channel}
        />
      ));
  };

  renderContent = () => {
    const {
      t,
      latestPosts,
      topicPosts,
      trendPosts,
      channels,
      banner,
      status,
      displayName,
      match: {
        params: { name },
      },
    } = this.props;

    const loading = status === LOADING;
    // video should have teaser url, article just show media url
    const showBanner =
      haveObjectProperties(banner) &&
      (!!banner.id && banner.type === VIDEO ? banner.teaserEmbedUrl : true);
    const showChannels = channels.length > 0;
    const showTrendPosts = !name && trendPosts.length > 0;
    const showTopicPosts = !name && topicPosts.length > 0;
    const showLatestPosts = latestPosts.length > 0;

    return (
      <main className={styles.root}>
        <div
          ref={node => {
            this.node = node;
          }}
          className={styles.container}
        >
          {!!name && (
            <React.Fragment>
              <Breadcrumb />
              <h1 className={styles.title}>{displayName}</h1>
            </React.Fragment>
          )}
          {showBanner && <Banner {...banner} />}
          {showLatestPosts && (
            <div className={styles.latestPostBlock}>
              <PostList
                posts={latestPosts}
                title={t('home:home.latestPost.title')}
              />
            </div>
          )}

          {showTrendPosts && <TrendBlock posts={trendPosts} />}

          {showChannels && (
            <section className={styles.channelBlock}>
              <h2 className={styles.title}>
                {t('home:home.editorsPick.title')}
              </h2>
              {this.renderChannels(channels)}
            </section>
          )}

          {loading && <SnapaskLoader />}
        </div>

        {showTopicPosts && (
          <PostList
            posts={topicPosts}
            title={t('home:home.moreTopics.title')}
          />
        )}
      </main>
    );
  };

  render() {
    const {
      t,
      channels,
      status,
      userId,
      isInApp,
      multipleOfferingMode,
    } = this.props;
    const showCtaFooter = !userId && !isInApp;
    const seoComponent = (
      <SEOHead
        title={t('seo.home.title')}
        description={t('seo.home.description')}
      />
    );

    if (__SERVER__) {
      return (
        <div style={{ display: 'none' }}>
          {seoComponent}
          {status === ERROR && <NotFound />}
          {this.renderChannels(channels)}
        </div>
      );
    }
    return (
      <MainLayout showFooter>
        {seoComponent}
        {status === ERROR ? <NotFound /> : this.renderContent()}
        {showCtaFooter && (
          <CTAFooter
            hasContent={multipleOfferingMode}
            title={t('home:home.banner.title')}
            subTitle={t('home:home.banner.description')}
            buttonText={t('home:home.banner.cta')}
          />
        )}
      </MainLayout>
    );
  }
}

export default compose(
  translate(['seo', 'channel', 'home']),
  connect(mapStateToProps, {
    resetHome,
    fetchNextPage,
    getMultipleOfferingMode,
  })
)(Home);



// WEBPACK FOOTER //
// ./app/containers/Home/Home.jsx